import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
const ENDPOINTS = {
  top: 'top',
  recent: 'recent',
  streaks: 'streaks'
};

/**
 * "Overall statistics" modal — reachable from the button next to mana on the
 * Arena profile page (see ArenaProfilePage.tsx). Three tabs, each backed by
 * its own endpoint reading arena_stats/arena_battles directly at request
 * time (GetTopStatsController / GetRecentMatchesController /
 * GetWinStreakTopController) — so an admin's "Recalculate" run in the admin
 * panel is reflected here immediately on next open/reload, with nothing to
 * invalidate.
 */
export default class StatsOverviewModal extends Modal {
  activeTab = 'top';
  loading = {
    top: false,
    recent: false,
    streaks: false
  };
  loaded = {
    top: false,
    recent: false,
    streaks: false
  };
  error = {
    top: null,
    recent: null,
    streaks: null
  };
  topRows = [];
  recentRows = [];
  streakRows = [];
  oninit(vnode) {
    super.oninit(vnode);
    this.loadTab('top');
  }
  className() {
    return 'ArenaStatsOverviewModal Modal--large';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-chart-column"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.title'));
  }
  switchTab(tab) {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    if (!this.loaded[tab] && !this.loading[tab]) {
      this.loadTab(tab);
    }
    m.redraw();
  }
  async loadTab(tab) {
    this.loading[tab] = true;
    this.error[tab] = null;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/stats/${ENDPOINTS[tab]}`,
        errorHandler: () => {} // see ArenaProfilePage.tsx saveDeck() — this.error[tab] already renders its own state
      });
      const data = response?.data ?? [];
      if (tab === 'top') this.topRows = data;else if (tab === 'recent') this.recentRows = data;else this.streakRows = data;
      this.loaded[tab] = true;
    } catch {
      this.error[tab] = app.translator.trans('forumaker-arena.forum.profile.stats_overview.error');
    } finally {
      this.loading[tab] = false;
      m.redraw();
    }
  }
  formatDate(iso) {
    if (!iso) return '—';
    const utcStr = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
    const d = new Date(utcStr);
    return d.toLocaleString(app.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /** Renders a player's name as a link to their profile when a username is known, plain text otherwise. */
  nameLink(name, username) {
    if (!name || !username) return name;
    const profileUrl = app.route('user', {
      username
    });
    return m("a", {
      className: "ArenaStatsOverviewModal-nameLink",
      href: profileUrl,
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
        m.route.set(profileUrl);
      }
    }, name);
  }
  renderTabBody() {
    const tab = this.activeTab;
    if (this.loading[tab] && !this.loaded[tab]) {
      return m(LoadingIndicator, null);
    }
    if (this.error[tab]) {
      return m("div", {
        className: "Alert Alert--error"
      }, m("i", {
        className: "fas fa-exclamation-circle"
      }), " ", this.error[tab]);
    }
    if (tab === 'top') return this.renderTop();
    if (tab === 'recent') return this.renderRecent();
    return this.renderStreaks();
  }
  renderTop() {
    if (this.topRows.length === 0) {
      return m("div", {
        className: "ArenaStatsOverviewModal-empty"
      }, m("i", {
        className: "fas fa-inbox"
      }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.empty'));
    }
    return m("table", {
      className: "ArenaStatsOverviewModal-table"
    }, m("thead", null, m("tr", null, m("th", null, "#"), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_player')), m("th", null, app.translator.trans('forumaker-arena.lib.stats.wins')), m("th", null, app.translator.trans('forumaker-arena.lib.stats.losses')), m("th", null, app.translator.trans('forumaker-arena.lib.stats.draws')), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats.rating')))), m("tbody", null, this.topRows.map((row, i) => m("tr", {
      key: row.userId
    }, m("td", null, this.rankBadge(i)), m("td", {
      className: "ArenaStatsOverviewModal-player"
    }, this.nameLink(row.displayName, row.username)), m("td", null, m("span", {
      className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--wins"
    }, row.wins)), m("td", null, m("span", {
      className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--losses"
    }, row.losses)), m("td", null, m("span", {
      className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--draws"
    }, row.draws)), m("td", null, m("span", {
      className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--rating"
    }, row.rating))))));
  }

  /** Plain neutral numbered circle for a leaderboard rank. */
  rankBadge(index) {
    return m("span", {
      className: "ArenaStatsOverviewModal-rank"
    }, index + 1);
  }
  renderRecent() {
    if (this.recentRows.length === 0) {
      return m("div", {
        className: "ArenaStatsOverviewModal-empty"
      }, m("i", {
        className: "fas fa-inbox"
      }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.empty'));
    }
    return m("table", {
      className: "ArenaStatsOverviewModal-table ArenaStatsOverviewModal-table--recent"
    }, m("thead", null, m("tr", null, m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_match')), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_result')), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_date')))), m("tbody", null, this.recentRows.map(row => {
      const inner = m('[', null, m("td", {
        className: "ArenaStatsOverviewModal-player"
      }, this.nameLink(row.challengerName, row.challengerUsername), " ", m("span", {
        className: "ArenaStatsOverviewModal-vs"
      }, "vs"), " ", this.nameLink(row.opponentName, row.opponentUsername)), m("td", null, row.isDraw ? m("span", {
        className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--draws"
      }, m("i", {
        className: "fas fa-handshake"
      }), " ", app.translator.trans('forumaker-arena.forum.battle_draw.title')) : m("span", {
        className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--wins"
      }, m("i", {
        className: "fas fa-trophy"
      }), " ", row.winnerName)), m("td", {
        className: "ArenaStatsOverviewModal-date"
      }, this.formatDate(row.startedAt)));
      return row.discussionId ? m("tr", {
        key: row.id,
        className: "ArenaStatsOverviewModal-clickableRow",
        onclick: () => {
          this.hide();
          m.route.set('/d/' + row.discussionId);
        }
      }, inner) : m("tr", {
        key: row.id
      }, inner);
    })));
  }
  renderStreaks() {
    if (this.streakRows.length === 0) {
      return m("div", {
        className: "ArenaStatsOverviewModal-empty"
      }, m("i", {
        className: "fas fa-inbox"
      }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.empty'));
    }
    return m("table", {
      className: "ArenaStatsOverviewModal-table"
    }, m("thead", null, m("tr", null, m("th", null, "#"), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_player')), m("th", null, app.translator.trans('forumaker-arena.forum.profile.stats_overview.col_streak')))), m("tbody", null, this.streakRows.map((row, i) => m("tr", {
      key: row.userId
    }, m("td", null, this.rankBadge(i)), m("td", {
      className: "ArenaStatsOverviewModal-player"
    }, this.nameLink(row.displayName, row.username)), m("td", null, m("span", {
      className: "ArenaStatsOverviewModal-pill ArenaStatsOverviewModal-pill--streak"
    }, m("i", {
      className: "fas fa-fire"
    }), " ", row.winStreak))))));
  }
  content() {
    return m("div", {
      className: "Modal-body ArenaStatsOverviewModal-body"
    }, m("div", {
      className: "ArenaStatsOverviewModal-tabs"
    }, m("button", {
      type: "button",
      className: `ArenaStatsOverviewModal-tab ${this.activeTab === 'top' ? 'active' : ''}`,
      onclick: () => this.switchTab('top')
    }, m("i", {
      className: "fas fa-ranking-star"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.tab_top')), m("button", {
      type: "button",
      className: `ArenaStatsOverviewModal-tab ${this.activeTab === 'recent' ? 'active' : ''}`,
      onclick: () => this.switchTab('recent')
    }, m("i", {
      className: "fas fa-clock-rotate-left"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.tab_recent')), m("button", {
      type: "button",
      className: `ArenaStatsOverviewModal-tab ${this.activeTab === 'streaks' ? 'active' : ''}`,
      onclick: () => this.switchTab('streaks')
    }, m("i", {
      className: "fas fa-fire"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.stats_overview.tab_streaks'))), m("div", {
      className: "ArenaStatsOverviewModal-content"
    }, this.renderTabBody()));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/StatsOverviewModal', StatsOverviewModal);